"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";

import { getHotelListContext } from "@/analytics/services/hotel/hotelListContextStorage";
import { useHotelAddToWishlistAnalytics } from "@/components/pages/hotel/search/hooks/useHotelAddToWishlistAnalytics";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useHotelFavorite } from "@/hooks/useHotelFavorite";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { buildFromDetailData } from "@/utils/hotel/buildFavoritedHotelPayload";
import { useLocale } from "next-intl";
import { useHotelShareAnalytics } from "./hooks/useHotelShareAnalytics";
import GalleryPopup, {
  DEFAULT_GALLERY_IMAGES,
  DEFAULT_GALLERY_META,
  DEFAULT_GALLERY_TABS,
} from "./GalleryPopup";
import HotelGallerySwiper from "./HotelGallerySwiper";

export default function GallerySection({
  hotelData,
  productForAnalytics,
  passengersForAnalytics,
  checkinDate,
  checkOutDate,
  roomsData,
  guests,
}) {
  const t = useTranslationDetector();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const { currency } = useCurrency();
  const locale = useLocale();
  const { trackShareHotel } = useHotelShareAnalytics();

  useEffect(() => {
    setIsClient(true);
  }, []);
  // API'den gelen medias'ı gallery formatına dönüştür
  const galleryData = useMemo(() => {
    const medias = hotelData?.Medias || [];
    const hotelName =
      hotelData?.HotelName || hotelData?.Name || DEFAULT_GALLERY_META.hotelName;

    // Medias'ı image formatına dönüştür
    const images =
      medias.length > 0
        ? medias.map((media, index) => ({
            id: media.Id?.toString() || `media-${index}`,
            src: media.Url || "",
            alt:
              media.Description || media.Category || `Hotel image ${index + 1}`,
            categoryIds: [
              "all",
              media.Category?.toLowerCase().replace(/\s+/g, "-") || "other",
            ].filter(Boolean),
          }))
        : DEFAULT_GALLERY_IMAGES.map((image) => ({ ...image }));

    // Category'lere göre tabs oluştur
    const categories = [
      ...new Set(medias.map((m) => m.Category).filter(Boolean)),
    ];
    const tabs =
      categories.length > 0
        ? [
            { id: "all", label: "Tümü" },
            ...categories.map((cat) => ({
              id: cat.toLowerCase().replace(/\s+/g, "-"),
              label: cat,
            })),
          ]
        : DEFAULT_GALLERY_TABS.map((tab) => ({ ...tab }));

    return {
      hotelName,
      ctaLabel: DEFAULT_GALLERY_META.ctaLabel,
      tabs,
      images,
    };
  }, [hotelData]);

  const previewHeroImage = galleryData.images[0];
  const previewThumbnails = galleryData.images.slice(1, 5);
  const totalImageCount = galleryData.images.length;

  const favoriteContext = useMemo(
    () => ({
      locale: locale ?? "tr",
      currency: currency ?? "TRY",
      checkinDate,
      checkoutDate: checkOutDate,
      roomsData,
      adultCount: guests?.adult ?? 1,
      childCount: guests?.child ?? 0,
      childrenAges: guests?.childrenAges ?? [],
    }),
    [
      locale,
      currency,
      checkinDate,
      checkOutDate,
      roomsData,
      guests?.adult,
      guests?.child,
      guests?.childrenAges?.join?.(","),
    ],
  );

  const { isFavorite, isAddingFavorite, handleFavoriteClick } =
    useHotelFavorite({
      hotelData,
      getPayload: buildFromDetailData,
      context: favoriteContext,
      hotelIdKey: "HotelId",
    });

  const listContext = getHotelListContext();
  const wishlistItemListId = listContext?.item_list_id ?? "hotel_detail";
  const wishlistItemListName = listContext?.item_list_name ?? "Hotel Detail";

  const { trackAddToWishlist } = useHotelAddToWishlistAnalytics({
    product: productForAnalytics,
    itemListId: wishlistItemListId,
    itemListName: wishlistItemListName,
    passengers: passengersForAnalytics,
    currency: productForAnalytics?.Currency ?? undefined,
    value: productForAnalytics?.price ?? undefined,
  });

  const handleOpenPopup = useCallback(() => {
    setIsPopupOpen(true);
  }, []);

  const handleShareClick = useCallback(
    (event) => {
      event.stopPropagation();

      trackShareHotel(galleryData.hotelName);
      if (typeof window === "undefined") return;

      const shareUrl = window.location.href;
      const shareTitle = galleryData.hotelName;

      if (navigator.share) {
        navigator
          .share({
            title: shareTitle,
            text: shareTitle,
            url: shareUrl,
          })
          .catch(() => {
            // kullanıcı share sheet'i iptal ederse hata atmasın
          });
      } else if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(shareUrl).catch(() => {});
      }
    },
    [galleryData.hotelName, trackShareHotel],
  );

  const handleMobileFavoriteClick = useCallback(
    (event) => {
      if (!isFavorite) {
        trackAddToWishlist();
      }
      handleFavoriteClick(event);
    },
    [handleFavoriteClick, isFavorite, trackAddToWishlist],
  );

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleOpenPopup();
      }
    },
    [handleOpenPopup],
  );

  return (
    <>
      <section
        className="cursor-pointer gap-4 md:grid md:grid-cols-[1.005fr_0.995fr]"
        role="button"
        tabIndex={0}
        onClick={handleOpenPopup}
        onKeyDown={handleKeyDown}
      >
        {/* Mobile: SSR'da Swiper bozuk görünebildiği için ilk render'da statik hero, client'ta Swiper'a upgrade */}
        <div className="md:hidden">
          <div className="relative w-full overflow-hidden rounded-none md:rounded-lg">
            <div className="absolute top-5 right-4 z-10 flex items-center gap-2.5">
              <button
                type="button"
                onClick={handleMobileFavoriteClick}
                disabled={isAddingFavorite}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(29,30,32,0.6)] disabled:opacity-50"
                aria-label={
                  isFavorite ? t("removeFromFavorites") : t("addToFavorites")
                }
              >
                <FontAwesomeIcon
                  className="!h-6 !w-6 translate-x-[0.5px] translate-y-[2px] leading-none text-white"
                  icon={
                    isFavorite
                      ? byPrefixAndName.fak["like-filled"]
                      : byPrefixAndName.fak["heart-1"]
                  }
                />
              </button>
              <button
                type="button"
                onClick={handleShareClick}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(29,30,32,0.6)]"
                aria-label="Paylaş"
              >
                <FontAwesomeIcon
                  className="!h-[19px] !w-[19px] text-white"
                  icon={byPrefixAndName.fak["upload"]}
                />
              </button>
            </div>

            {isClient ? (
              <>
                <HotelGallerySwiper images={galleryData.images} />
              </>
            ) : (
              <div className="relative aspect-[619/414] w-full overflow-hidden rounded-none md:rounded-lg">
                {previewHeroImage && (
                  <Image
                    src={previewHeroImage.src}
                    alt={previewHeroImage.alt}
                    fill
                    unoptimized
                    sizes="100vw"
                    className="object-cover"
                    priority
                  />
                )}
              </div>
            )}
            <span className="absolute right-4 bottom-4 z-10 flex items-center justify-center gap-1 rounded-full bg-[rgba(29,30,32,0.6)] p-2 text-white">
              <FontAwesomeIcon icon={byPrefixAndName.far["image"]} />
              <span className="text-base/21 lg:block">{totalImageCount}+</span>
            </span>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden md:contents">
          <div className="md:col-start-1 md:col-end-2">
            <div
              className="relative w-full overflow-hidden rounded-none md:rounded-lg"
              style={{ aspectRatio: "619 / 414" }}
            >
              {previewHeroImage && (
                <Image
                  src={previewHeroImage.src}
                  alt={previewHeroImage.alt}
                  fill
                  unoptimized
                  sizes="(min-width: 768px) 56vw, 100vw"
                  className="object-cover"
                  priority
                />
              )}
            </div>
          </div>

          <div className="grid h-full grid-rows-2 gap-4 md:col-start-2 md:col-end-3">
            <div className="grid h-full grid-cols-2 gap-4">
              {previewThumbnails.slice(0, 2).map((image) => (
                <div
                  key={image.id}
                  className="relative h-full w-full overflow-hidden rounded-none md:rounded-lg"
                  style={{ aspectRatio: "298 / 199" }}
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    unoptimized
                    sizes="(min-width: 768px) 25vw, 50vw"
                    className="object-cover"
                  />
                </div>
              ))}
            </div>

            <div className="grid h-full grid-cols-2 gap-4">
              {previewThumbnails.slice(2).map((image, index, array) => {
                const isLast = index === array.length - 1;
                return (
                  <div
                    key={image.id}
                    className="relative h-full w-full overflow-hidden rounded-none md:rounded-lg"
                    style={{ aspectRatio: "298 / 199" }}
                  >
                    <Image
                      src={image.src}
                      alt={image.alt}
                      fill
                      sizes="(min-width: 768px) 25vw, 50vw"
                      className="object-cover"
                    />
                    {isLast && (
                      <div className="absolute inset-0 flex items-end justify-end p-4">
                        <button
                          type="button"
                          className="flex items-center gap-[10px] rounded-full bg-[rgba(29,30,32,0.8)] px-3 py-2 text-base font-medium text-white"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenPopup();
                          }}
                        >
                          <span className="flex !h-6 !w-6 items-center justify-center px-[2.4px] py-[3.6px]">
                            <FontAwesomeIcon
                              icon={byPrefixAndName.far["image"]}
                            />
                          </span>
                          <span className="hidden text-base/21 lg:block">
                            {totalImageCount}+ {t("hotel.photo")}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <GalleryPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        gallery={galleryData}
      />
    </>
  );
}
